import { type ApolloClient } from '@apollo/client';

import depotResetPollQuery from 'pages/Management/Account/Depots/ResetDepotAssetsModal/hooks/depotResetPoll.graphql';
import { buildDepotSummaryCounts } from 'pages/Management/Account/Depots/ResetDepotAssetsModal/utils/depotSummaryCounts';
import contactBulkImportPollQuery from 'pages/Management/Contact/BulkCreateContactsModal/hooks/contactBulkImportPoll.graphql';
import depotBulkImportPollQuery from 'pages/Management/Depots/BulkCreateDepotModal/hooks/depotBulkImportPoll.graphql';
import assetBulkUploadPollQuery from 'pages/Management/Fleets/BulkAddAssetModal/hooks/assetBulkUploadPoll.graphql';
import { buildSummary } from 'pages/Management/Fleets/BulkAddAssetModal/utils/buildAssetSummary';

import {
  assetToastCopy,
  contactToastCopy,
  depotResetToastCopy,
  depotToastCopy,
  type ImportOutcome,
  type ToastCopy,
} from './copy';

const PROCESSING = 'PROCESSING';

export interface ImportResult extends ImportOutcome {
  reportUrl?: string;
}

interface Resolver {
  /** Resolves to null when the record is gone or hasn't finished after all. */
  fetch: (
    client: ApolloClient<object>,
    id: string,
  ) => Promise<ImportResult | null>;
  copy: (outcome: ImportOutcome) => ToastCopy;
}

// Reuses each wizard's own poll document so the toast reports exactly what the
// wizard's summary step would have. It also means `reportUrl` is resolved in a
// real request — the API builds it from the request's base url, so it can't be
// carried on the event, which is published from a background job.
type BulkImportRecord =
  | {
      status: string;
      reportUrl?: string | null;
      summary?: { successful: number; failed: number } | null;
    }
  | null
  | undefined;

function normalizeBulkImport(record: BulkImportRecord): ImportResult | null {
  if (!record || record.status === PROCESSING) return null;

  return {
    added: record.summary?.successful ?? 0,
    failed: record.summary?.failed ?? 0,
    reportUrl: record.reportUrl ?? undefined,
  };
}

/** Keyed by the `import_type` the API puts in the event payload. */
export const RESOLVERS: Record<string, Resolver> = {
  depot_bulk_import: {
    fetch: async (client, id) => {
      const { data } = await client.query<
        DepotBulkImportPollQuery,
        DepotBulkImportPollQueryVariables
      >({
        query: depotBulkImportPollQuery,
        variables: { id },
        fetchPolicy: 'network-only',
      });

      return normalizeBulkImport(data?.depotBulkImport);
    },
    copy: depotToastCopy,
  },

  contact_bulk_import: {
    fetch: async (client, id) => {
      const { data } = await client.query<
        ContactBulkImportPollQuery,
        ContactBulkImportPollQueryVariables
      >({
        query: contactBulkImportPollQuery,
        variables: { id },
        fetchPolicy: 'network-only',
      });

      return normalizeBulkImport(data?.contactBulkImport);
    },
    copy: contactToastCopy,
  },

  asset_bulk_upload: {
    fetch: async (client, id) => {
      const { data } = await client.query<
        AssetBulkUploadPollQuery,
        AssetBulkUploadPollQueryVariables
      >({
        query: assetBulkUploadPollQuery,
        variables: { id },
        fetchPolicy: 'network-only',
      });

      const upload = data?.assetBulkUpload;
      if (!upload || upload.status === PROCESSING) return null;

      // Assets has no server-side summary; counts come from the per-depot
      // registrations, same as the wizard.
      const summary = buildSummary(upload, false);

      return {
        added: summary.assetsAddedCount,
        failed: summary.assetsFailedCount,
        reportUrl: summary.reportUrl,
      };
    },
    copy: assetToastCopy,
  },

  depot_reset: {
    fetch: async (client, id) => {
      const { data } = await client.query<
        DepotResetPollQuery,
        DepotResetPollQueryVariables
      >({
        query: depotResetPollQuery,
        variables: { id },
        fetchPolicy: 'network-only',
      });

      const reset = data?.depotReset;
      if (!reset || (reset.status !== 'COMPLETED' && reset.status !== 'FAILED'))
        return null;

      // Same tally as the modal's SummaryStep: a fully successful reset
      // reports every depot, otherwise only those applied successfully.
      const { depotCount, succeeded, withErrors } = buildDepotSummaryCounts(
        reset.depotSummaries,
      );
      return {
        added: reset.outcome === 'SUCCESS' ? depotCount : succeeded,
        failed: withErrors,
        outcome: reset.outcome ?? undefined,
        reportUrl: reset.reportUrl ?? undefined,
      };
    },
    copy: depotResetToastCopy,
  },
};
